/**
 * Cuándo cierra la convocatoria de origen del expediente (issue #629).
 *
 * El expediente solo decía el título de la convocatoria. Un borrador podía
 * seguir abierto mientras el plazo vencía, sin que nada lo avisara. Esta
 * línea da la fecha y cuánto falta, o dice que ya cerró.
 *
 * El conteo de días es el de `classifyUrgency` (el mismo del Radar), y
 * `closes_at` se parsea como día calendario local (#537).
 *
 * Una convocatoria "hasta agotar cupos" (#692) no tiene fecha de cierre: la
 * línea lo dice, porque los cupos se acaban sin aviso.
 */
import { classifyUrgency } from "@/components/UrgencyBadge";
import { formatDateEs } from "@/lib/dates";

export function CierreConvocatoria({
  closesAt,
  untilQuotaExhausted = false,
  now,
}: {
  closesAt: string | null | undefined;
  /** La convocatoria recibe postulaciones hasta agotar cupos (#692). */
  untilQuotaExhausted?: boolean;
  /** Para tests deterministas. */
  now?: Date;
}) {
  if (!closesAt && untilQuotaExhausted) {
    return (
      <span
        data-testid="cierre-convocatoria"
        data-cerrada="false"
        data-cupos="true"
        className="ml-1 font-semibold text-amber-800"
      >
        · Hasta agotar cupos: radica cuanto antes
      </span>
    );
  }
  if (!closesAt) return null;
  const urgencia = classifyUrgency(closesAt, now);
  if (urgencia.daysUntilClose === null) return null;

  const fecha = formatDateEs(closesAt, { day: "numeric", month: "long", year: "numeric" });
  const dias = urgencia.daysUntilClose;
  const cerrada = dias < 0;
  const falta = dias === 0 ? "hoy" : dias === 1 ? "mañana" : `en ${dias} días`;

  return (
    <span
      data-testid="cierre-convocatoria"
      data-cerrada={cerrada ? "true" : "false"}
      className={
        "ml-1 whitespace-nowrap " +
        (cerrada
          ? "font-semibold text-amber-800"
          : dias <= 7
            ? "font-semibold text-red-700"
            : "text-slate-600")
      }
    >
      · {cerrada ? `Cerró el ${fecha}` : `Cierra el ${fecha} · ${falta}`}
    </span>
  );
}
